import { useState, useEffect } from 'react';
import { BarChart3, Home, Users, FileText, MessageSquare, TrendingUp, Building2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AdminStatsPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/admin/analytics`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStats(response.data);
      } catch (error) {
        toast.error('Erreur lors du chargement des statistiques');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return (
      <div data-testid="loading-spinner" className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-lg text-muted-foreground">Chargement...</div>
      </div>
    );
  }

  return (
    <div data-testid="admin-stats-page" className="min-h-screen pt-20 pb-24 px-6 md:px-12 lg:px-24">
      <div className="max-w-7xl mx-auto space-y-12">
        <div className="pt-12 space-y-4">
          <div className="flex items-center gap-3">
            <BarChart3 className="w-10 h-10 text-primary" />
            <h1 className="text-5xl md:text-6xl font-serif tracking-tight leading-none">Statistiques</h1>
          </div>
          <p className="text-base md:text-lg leading-relaxed text-muted-foreground">
            Vue d'ensemble de votre activité immobilière
          </p>
        </div>

        {stats && (
          <>
            {/* Main Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card className="border-border hover:shadow-lg transition-shadow">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium uppercase tracking-widest text-muted-foreground">
                    Total Biens
                  </CardTitle>
                  <Building2 className="h-5 w-5 text-primary" />
                </CardHeader>
                <CardContent>
                  <div className="text-4xl font-serif font-bold">{stats.properties.total}</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    biens dans le portefeuille
                  </p>
                </CardContent>
              </Card>

              <Card className="border-border hover:shadow-lg transition-shadow">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium uppercase tracking-widest text-muted-foreground">
                    À Vendre
                  </CardTitle>
                  <Home className="h-5 w-5 text-green-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-4xl font-serif font-bold text-green-600">{stats.properties.for_sale}</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    biens en vente
                  </p>
                </CardContent>
              </Card>

              <Card className="border-border hover:shadow-lg transition-shadow">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium uppercase tracking-widest text-muted-foreground">
                    À Louer
                  </CardTitle>
                  <FileText className="h-5 w-5 text-blue-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-4xl font-serif font-bold text-blue-600">{stats.properties.for_rent}</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    biens en location
                  </p>
                </CardContent>
              </Card>

              <Card className="border-border hover:shadow-lg transition-shadow">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium uppercase tracking-widest text-muted-foreground">
                    Vendus
                  </CardTitle>
                  <TrendingUp className="h-5 w-5 text-amber-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-4xl font-serif font-bold text-amber-600">{stats.properties.sold}</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    ventes réalisées
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Secondary Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="border-border hover:shadow-lg transition-shadow">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium uppercase tracking-widest text-muted-foreground">
                    Clients
                  </CardTitle>
                  <Users className="h-5 w-5 text-primary" />
                </CardHeader>
                <CardContent>
                  <div className="text-4xl font-serif font-bold">{stats.clients.total}</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    clients enregistrés
                  </p>
                </CardContent>
              </Card>

              <Card className="border-border hover:shadow-lg transition-shadow">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium uppercase tracking-widest text-muted-foreground">
                    Gestions Locatives
                  </CardTitle>
                  <FileText className="h-5 w-5 text-primary" />
                </CardHeader>
                <CardContent>
                  <div className="text-4xl font-serif font-bold">{stats.rentals.active}</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    locations actives
                  </p>
                </CardContent>
              </Card>

              <Card className="border-border hover:shadow-lg transition-shadow">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium uppercase tracking-widest text-muted-foreground">
                    Demandes de Contact
                  </CardTitle>
                  <MessageSquare className="h-5 w-5 text-primary" />
                </CardHeader>
                <CardContent>
                  <div className="text-4xl font-serif font-bold">{stats.contacts.total}</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    messages reçus
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Recent Contacts */}
            {stats.contacts.recent && stats.contacts.recent.length > 0 && (
              <div className="space-y-6">
                <h2 className="text-2xl font-serif tracking-tight">Dernières demandes de contact</h2>
                <div className="bg-white border border-border overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-secondary">
                      <tr>
                        <th className="text-left p-4 text-xs uppercase tracking-widest text-muted-foreground font-semibold">Nom</th>
                        <th className="text-left p-4 text-xs uppercase tracking-widest text-muted-foreground font-semibold">Email</th>
                        <th className="text-left p-4 text-xs uppercase tracking-widest text-muted-foreground font-semibold">Téléphone</th>
                        <th className="text-left p-4 text-xs uppercase tracking-widest text-muted-foreground font-semibold">Message</th>
                        <th className="text-left p-4 text-xs uppercase tracking-widest text-muted-foreground font-semibold">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.contacts.recent.map((contact, idx) => (
                        <tr key={contact.id || idx} className="border-t border-border hover:bg-secondary/50 transition-colors">
                          <td className="p-4 font-semibold">{contact.full_name}</td>
                          <td className="p-4">{contact.email}</td>
                          <td className="p-4">{contact.phone}</td>
                          <td className="p-4 max-w-xs truncate">{contact.message}</td>
                          <td className="p-4 text-sm text-muted-foreground">
                            {new Date(contact.created_at).toLocaleDateString('fr-BE')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default AdminStatsPage;
